// Minimal Toast context matching the cloudfront_ui ToastContext API
// so that blyn/layout.tsx works without modification.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

export interface ToastMessage {
  id: string
  kind: 'success' | 'warning' | 'error' | 'info'
  title: string
  message: string
}

interface ToastCtx {
  success: (message: string, title?: string) => void
  warning: (message: string, title?: string) => void
  error: (message: string, title?: string) => void
  info: (message: string, title?: string) => void
}

export const ToastContext = createContext<ToastCtx>({
  success: () => {},
  warning: () => {},
  error: () => {},
  info: () => {},
})

// (BLYN-F5) Error toasts are rate-limited so a burst of failures (a flaky
// network, a batch API call that fails N times) can't flood the UI with
// overlapping toasts. Errors are grouped into a "type" by their title —
// at most one toast per type is shown per throttle window; duplicates
// inside that window are logged (never silently dropped) and rolled into
// a single trailing toast reporting how many were suppressed. Distinct
// error types (different titles) are never throttled against each other.
const ERROR_THROTTLE_MS = 3000

interface ErrorThrottleEntry {
  suppressed: number
  lastMessage: string
  timer: ReturnType<typeof setTimeout>
}

export function ToastProvider({ children, onToast }: { children: ReactNode; onToast?: (t: ToastMessage) => void }) {
  const [, setToasts] = useState<ToastMessage[]>([])
  const errorThrottleRef = useRef<Map<string, ErrorThrottleEntry>>(new Map())

  // Clear any pending trailing-toast timers on unmount.
  useEffect(() => {
    const throttle = errorThrottleRef.current
    return () => {
      throttle.forEach((entry) => clearTimeout(entry.timer))
      throttle.clear()
    }
  }, [])

  const push = useCallback((kind: ToastMessage['kind'], message: string, title = '') => {
    const t: ToastMessage = { id: crypto.randomUUID(), kind, title, message }
    setToasts((prev) => [...prev, t])
    onToast?.(t)
  }, [onToast])

  const pushError = useCallback((message: string, title = '') => {
    // Never lose error information, even when the toast itself is suppressed.
    console.error(`[toast:error]${title ? ` ${title}:` : ''} ${message}`)

    const key = title || message
    const throttle = errorThrottleRef.current
    const existing = throttle.get(key)

    if (existing) {
      // Within the throttle window for this error type — suppress the toast,
      // just track it so the trailing toast can report the count.
      existing.suppressed += 1
      existing.lastMessage = message
      return
    }

    // Leading edge: first occurrence of this error type shows immediately.
    push('error', message, title)

    const entry: ErrorThrottleEntry = {
      suppressed: 0,
      lastMessage: message,
      timer: setTimeout(() => {
        throttle.delete(key)
        if (entry.suppressed > 0) {
          // Trailing edge: report what got collapsed during the window.
          const aggregatedTitle = title ? `${title} (+${entry.suppressed} more)` : `+${entry.suppressed} more`
          push('error', entry.lastMessage, aggregatedTitle)
        }
      }, ERROR_THROTTLE_MS),
    }
    throttle.set(key, entry)
  }, [push])

  const ctx: ToastCtx = {
    success: (m, t) => push('success', m, t),
    warning: (m, t) => push('warning', m, t),
    error: (m, t) => pushError(m, t),
    info: (m, t) => push('info', m, t),
  }

  return <ToastContext.Provider value={ctx}>{children}</ToastContext.Provider>
}

export function useToast() {
  return useContext(ToastContext)
}
